import { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { Gallery } from "iconsax-reactjs";
import Button from "./Button";

type StoriesModalProps = {
  imageSrc?: string;
  open: boolean;
  onClose: () => void;
  onApply: (file: File, previewUrl: string) => void;
  aspect?: number;
};

export default function UploadCardCrooperModal({ imageSrc, open, onClose, onApply, aspect = 1 }: StoriesModalProps) {

  const MIN_ZOOM = 1;
  const MAX_ZOOM = 3;

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [open]);

  if (!open) return null;

  const getCroppedFile = async (src: string, crop: any) => {
    const img = new Image();
    img.src = src;
    await new Promise(r => (img.onload = r));

    const canvas = document.createElement("canvas");
    canvas.width = crop.width;
    canvas.height = crop.height;

    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(
      img,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      crop.width,
      crop.height
    );

    const blob: Blob = await new Promise(resolve =>
      canvas.toBlob(b => resolve(b!), "image/jpeg", 0.9)
    );

    const file = new File(
      [blob],
      `cropped-${Date.now()}.jpg`,
      { type: "image/jpeg" }
    );

    const previewUrl = URL.createObjectURL(blob);

    return { file, previewUrl };
  };

  const handleApply = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    const { file, previewUrl } = await getCroppedFile(
      imageSrc,
      croppedAreaPixels
    );
    onApply(file, previewUrl);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"/>
      <div className="relative bg-white rounded-2xl shadow-xl p-4 flex flex-col gap-6">
        <h3 className="text-gray-600 font-medium text-base leading-7">
          بریدن تصویر شاخص
        </h3>

        <div className="relative w-[430px] h-[430px] bg-gray-100 rounded-lg overflow-hidden">
          <Cropper
            image={imageSrc}
            aspect={aspect}
            crop={crop}
            zoom={zoom}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
          />
        </div>

        <div className="flex items-center gap-3" dir="ltr">
          <Gallery variant="Outline" color="#1E1E1E" size="18"/>
          <input
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="range-green w-full"
            style={{
              ["--value" as any]: `${((zoom - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)) * 100}%`,
            }}
          />
          <Gallery variant="Outline" color="#1E1E1E" size="26"/>
        </div>

        <div className="flex flex-row justify-end gap-4">
          <Button
            variant="gray"
            style="solid"
            size="lg"
            text="انصراف"
            loading={false}
            disabled={false}
            onClick={onClose}
          />
          <Button
            variant="primary"
            style="solid"
            size="lg"
            text="اعمال"
            loading={false}
            disabled={false}
            onClick={handleApply}
          />
        </div>
      </div>
    </div>
  );
};